import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  publishDate: string;
  readCount: number;
  commentCount: number;
  imageUrl: string;
  isHot?: boolean;
  isNew?: boolean;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  category,
  categoryName,
  publishDate,
  readCount,
  commentCount,
  imageUrl,
  isHot,
  isNew,
  variant = 'default',
}: ArticleCardProps) {
  const formatCount = (count: number): string => {
    if (count >= 10000) {
      return (count / 10000).toFixed(1) + '万';
    }
    return count.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors no-underline"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(readCount)}
            </span>
            <span>{publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 no-underline"
      >
        <div className="relative aspect-video overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {isHot && (
              <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium">
                <i className="ri-fire-line text-xs"></i>热门
              </span>
            )}
            {isNew && (
              <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium">
                <i className="ri-flashlight-line text-xs"></i>新瓜
              </span>
            )}
          </div>
        </div>
        <div className="p-4 md:p-5">
          <h3 className="text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
            {title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
            {excerpt}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 font-medium">
              {categoryName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(readCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {formatCount(commentCount)}
            </span>
            <span className="ml-auto">{publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 no-underline"
    >
      <div className="relative w-full sm:w-48 h-36 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {isHot && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary-500 text-white text-[10px] font-medium">
              <i className="ri-fire-line text-[10px]"></i>热
            </span>
          )}
          {isNew && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-[10px] font-medium">
              <i className="ri-flashlight-line text-[10px]"></i>新
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-1.5">
            {title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {excerpt}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500 mt-3">
          <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
            {categoryName}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line"></i>
            {formatCount(readCount)}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-3-line"></i>
            {formatCount(commentCount)}
          </span>
          <span className="ml-auto whitespace-nowrap">{publishDate}</span>
        </div>
      </div>
    </Link>
  );
}